import {init, onTTVC, start as startTTVC} from '@dropbox/ttvc';
import {ROUTE_PATHS} from 'constant';
import {useEffect, useRef} from 'react';
import {useLocation, useNavigation} from 'react-router';

import {
  currentRoute,
  isProfileRoute,
  isTeamRoute,
  isWorkstreamActivityRoute,
  isWorkstreamDetailRoute,
  isWorkstreamRoute,
} from './routeInfo';

// List of supported routes to collect TTVC information.
const supportedRoutes = [
  ROUTE_PATHS.PEOPLE,
  ROUTE_PATHS.TEAMS,
  ROUTE_PATHS.SETTINGS,
  ROUTE_PATHS.HOME,
  // The workstreams page's places (the list, the rail's other places, a card,
  // a full page); a person or pillar rides in the query, so it's the list.
  '/workstreams',
  '/workstreams/company',
  '/workstreams/inbox',
  '/workstreams/audit',
  '/workstreams/id',
  '/workstreams/id/detail',
  '/workstream/id',
  '/teams/name',
  '/people/name',
] as const;

export function loadTTVCTracker() {
  // Track TTVC
  init({debug: false, idleTimeout: 2000});
  onTTVC(async ({start, duration: realDuration}) => {
    // round the duration to avoid long decimal numbers
    const duration = Math.round(realDuration);

    // lazily import the amplitude client to not impact ttvc
    const {analyticsLogger} = await import('../analytics/analyticsLogger');

    // normalized route to report to amplitude
    const route = normalizedRoute();
    if (!route) {
      return;
    }

    // start is 0 the first time this is called.
    const subsequent = start !== 0;
    if (subsequent) {
      analyticsLogger().logEvent('TTVC_SUBSEQUENT', {duration, route});
    } else {
      analyticsLogger().logEvent('TTVC_INIT', {duration, route});
    }
  });
}

/**
 * Start a TTVC measurement on each in-app route change (TTVC_SUBSEQUENT); the
 * first page load is measured from navigation start without one. A change
 * waiting on a route loader starts when it begins, so the loader's time counts.
 * Only the path counts: query changes (filters, search text) aren't navigations.
 */
export const useTTVCRouteChanges = () => {
  const location = useLocation();
  const navigation = useNavigation();
  const pathname = navigation.location?.pathname ?? location.pathname;
  const startedFor = useRef(pathname);

  useEffect(() => {
    if (pathname === startedFor.current) {
      return;
    }
    startedFor.current = pathname;
    startTTVC();
  }, [pathname]);
};

export const normalizedRoute = () => {
  let route = currentRoute();
  if (isTeamRoute()) {
    route = '/teams/name';
  } else if (isProfileRoute()) {
    route = '/people/name';
  } else if (isWorkstreamRoute()) {
    route = '/workstreams/id';
  } else if (isWorkstreamDetailRoute()) {
    route = '/workstreams/id/detail';
  } else if (isWorkstreamActivityRoute()) {
    route = '/workstream/id/activity';
  }

  if (supportedRoutes.includes(route)) {
    return route;
  }

  return undefined;
};
